import { PlayerRef } from '@remotion/player';
import { TimelineWriteOnlyContext } from '../context-provider';
import { makeContentItem } from '../items/make-item-wkwk';
import { makeItem } from '../items/make-item';
import { captureInsertionTarget } from '@waka/compat/incoming-media';
import { commitIncomingMedia } from '@waka/compat/commit-incoming-media';
import { generateRandomId } from '../utils/generate-random-id';
import { TrackType, UndoableState } from '../state/types';
import { setLocalUrl } from '@/features/remotion-editor/editor/caching/load-to-blob-url';
import { cacheAssetLocally } from '@/features/remotion-editor/editor/caching/indexeddb';
import { ContentClipInfo, CLIP_TYPES } from '@/shared/apis/editor/clip-types';
import { DtoProjectType } from '@/shared/constants';
import type { EditorStarterAsset } from './assets';

export type DropPosition = {
  x: number;
  y: number;
};

const innerAddAsset = async ({
  file,
  contentId,
  clip,
  timelineWriteContext,
  playerRef,
  dropPosition,
  fps,
  compositionWidth,
  compositionHeight,
  tracks,
  filename,
  projectType,
  isAsset,
  extension,
  timelineStartFrame,
  targetTrackIndex,
  createTrack,
  staticPath,
  durationInSeconds,
  muteAudio,
  sourceRef,
  expectedUndoableState,
}: {
  file: Blob;
  contentId: string;
  clip: ContentClipInfo;
  timelineWriteContext: TimelineWriteOnlyContext;
  playerRef: React.RefObject<PlayerRef | null>;
  dropPosition: DropPosition | null;
  fps: number;
  compositionWidth: number;
  compositionHeight: number;
  tracks: TrackType[];
  filename: string;
  projectType: DtoProjectType;
  isAsset: boolean;
  extension: string | null;
  /** 타임라인에서 아이템이 시작될 프레임 */
  timelineStartFrame?: number;
  /** 드롭할 타임라인 트랙 인덱스 (null이면 front 위치에 추가) */
  targetTrackIndex?: number | null;
  createTrack?: boolean;
  /** public 폴더 내 정적 파일 경로 (효과음 등) */
  staticPath?: string | null;
  /** 아이템 duration 고정값 (선택, 이미지/GIF에서 사용) */
  durationInSeconds?: number;
  /** 원본 비디오 음성을 숨겨야 하는 agent workflow 입력에서 사용 */
  muteAudio?: boolean;
  /** 절대경로 대신 저장되는 원본 참조 */
  sourceRef?: EditorStarterAsset['sourceRef'];
  /** Optional source document for async catalog insertion; rejected if editing changed. */
  expectedUndoableState?: UndoableState;
}) => {
  const target = captureInsertionTarget(tracks, targetTrackIndex, createTrack);
  const from = timelineStartFrame ?? playerRef.current?.getCurrentFrame() ?? 0;
  // Internal cache identity is independent of the backend content id; a new import never replaces an old Blob.
  const assetId = 'media-' + generateRandomId(20);

  // A library entry can lack duration. Read the file rather than creating a one-frame clip.
  // Nonzero authored timeline/source windows still use the original company clip contract.
  const needsFullSourceProbe =
    (clip.clipType === CLIP_TYPES.VIDEO ||
      clip.clipType === CLIP_TYPES.AUDIO) &&
    !(clip.videoStart ?? 0) &&
    !(clip.start ?? 0) &&
    !(clip.videoEnd && clip.videoEnd > 0);
  const { item, asset } = needsFullSourceProbe
    ? await makeItem({
        file,
        fps,
        compositionWidth,
        compositionHeight,
        playerRef,
        dropPosition,
        remoteUrl: clip.contentPublicUri ?? null,
        remoteFileKey: null,
        filename,
      })
    : await makeContentItem({
        file,
        assetId,
        clip,
        fps,
        compositionWidth,
        compositionHeight,
        playerRef,
        dropPosition,
        remoteUrl: clip.contentPublicUri ?? null,
        remoteFileKey: null,
        filename,
        projectType,
        timelineStartFrame: from,
        durationInSeconds,
      });
  if (needsFullSourceProbe) {
    if (item.type !== (clip.clipType === CLIP_TYPES.AUDIO ? 'audio' : 'video'))
      throw new Error(
        '자료 종류와 원본 파일이 맞지 않습니다. 원본 파일을 다시 선택해 주세요.',
      );
    item.from = from;
  }
  if (muteAudio && item.type === 'video') {
    item.muteAudio = true;
  }
  asset.isAsset = isAsset;
  asset.extension = extension;
  asset.staticPath = staticPath ?? null;
  asset.sourceRef = sourceRef ?? null;
  // contentId 가 숫자 문자열이면 BE Content.id 로 보존 (export 시 BE 조회 키).
  // GTS_STEP TTS 처럼 UUID 인 경우는 contentId 비워둠 → 원본 remoteUrl 사용.
  const numericContentId = /^\d+$/.test(contentId)
    ? Number.parseInt(contentId, 10)
    : NaN;
  if (Number.isInteger(numericContentId)) {
    asset.contentId = numericContentId;
  }

  commitIncomingMedia(timelineWriteContext, item, asset, target, fps, expectedUndoableState);
};

export const addAsset = async ({
  file,
  contentId,
  filename,
  clip,
  timelineWriteContext,
  playerRef,
  dropPosition,
  fps,
  compositionWidth,
  compositionHeight,
  tracks,
  projectType,
  isAsset = false,
  extension = null,
  timelineStartFrame,
  targetTrackIndex,
  createTrack,
  staticPath = null,
  durationInSeconds,
  muteAudio,
  sourceRef,
  expectedUndoableState,
}: {
  file: Blob;
  contentId: string;
  filename: string;
  clip: ContentClipInfo;
  timelineWriteContext: TimelineWriteOnlyContext;
  playerRef: React.RefObject<PlayerRef | null>;
  dropPosition: DropPosition | null;
  fps: number;
  compositionWidth: number;
  compositionHeight: number;
  tracks: TrackType[];
  projectType: DtoProjectType;
  isAsset?: boolean;
  extension?: string | null;
  /** 타임라인에서 아이템이 시작될 프레임 */
  timelineStartFrame?: number;
  /** 드롭할 타임라인 트랙 인덱스 (null이면 front 위치에 추가) */
  targetTrackIndex?: number | null;
  createTrack?: boolean;
  /** public 폴더 내 정적 파일 경로 (효과음 등) */
  staticPath?: string | null;
  /** 아이템 duration 고정값 (선택, 이미지/GIF에서 사용) */
  durationInSeconds?: number;
  /** 원본 비디오 음성을 숨겨야 하는 agent workflow 입력에서 사용 */
  muteAudio?: boolean;
  /** 절대경로 대신 저장되는 원본 참조 */
  sourceRef?: EditorStarterAsset['sourceRef'];
  expectedUndoableState?: UndoableState;
}) => {
  // 호출자가 try/catch + classifyUploadFailure 로 사용자 안내 토스트를 띄운다.
  // 여기서 raw 영어 메시지를 그대로 토스트로 띄우면 분류기 우회로 본 PR 의 i18n 가치가 무효화된다.
  await innerAddAsset({
    file,
    contentId,
    fps,
    clip,
    compositionWidth,
    compositionHeight,
    tracks,
    timelineWriteContext,
    playerRef,
    dropPosition,
    filename,
    projectType,
    isAsset,
    extension,
    timelineStartFrame,
    targetTrackIndex,
    createTrack,
    staticPath,
    durationInSeconds,
    muteAudio,
    sourceRef,
    expectedUndoableState,
  });
};

export const addAssetOnly = async ({
  file,
  contentId,
}: {
  file: Blob;
  contentId: string;
}) => {
  const assetId = contentId;
  const url = URL.createObjectURL(file);
  setLocalUrl(assetId, url);

  await cacheAssetLocally({
    assetId: assetId,
    value: file,
  });
};
